import { atlPageQuery, useVoidAtlPageMutation } from "@common/api/atl"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { WO_PAGES } from "@common/flags"
import { handleError } from "@common/utils"
import { useQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  useCanGoBack,
  useNavigate,
  useRouter,
} from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"
import { requireFeatureFlag } from "@/lib/require-feature-flag"
import { CLOSED_SCAN } from "../-components/source"

export const Route = createFileRoute(
  "/_layout/aircraft_/$aircraftId/tech-log/void/$pageId",
)({
  beforeLoad: ({ context: { queryClient } }) =>
    requireFeatureFlag(queryClient, WO_PAGES),
  component: VoidPageDialog,
})

function VoidPageDialog(): React.ReactElement {
  const { aircraftId, pageId } = Route.useParams()
  const { data: page } = useQuery(atlPageQuery({ atl_page_id: pageId }))
  const mutation = useVoidAtlPageMutation()
  const navigate = useNavigate({ from: Route.fullPath })
  const router = useRouter()
  const canGoBack = useCanGoBack()
  const showToast = useCustomToast()
  const label = page?.log_number ?? "this page"
  const toList = () =>
    navigate({
      to: "/aircraft/$aircraftId",
      params: { aircraftId },
      search: (previous) => ({
        ...previous,
        tab: "tech-log" as const,
        ...CLOSED_SCAN,
        wide: undefined,
      }),
    })
  const back = () => (canGoBack ? router.history.back() : toList())

  const confirm = () =>
    mutation.mutate(
      { atl_page_id: pageId },
      {
        onSuccess: () => {
          showToast(`Voided ${label}`, "Removed from the tech log", "success")
          toList()
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <ConfirmDialog
      title={`Void ${label}?`}
      description="The page leaves the tech log and its flying comes off the aircraft's hours and cycles. Deferrals it cleared reopen, and deferrals only it raised are withdrawn."
      action="Void page"
      pendingAction="Voiding…"
      pending={mutation.isPending}
      disabled={!page}
      onConfirm={confirm}
      onCancel={back}
    />
  )
}
